/**
 * Rem 适配工具
 * 基于父元素（容器）大小进行 rem 自适应
 *
 * 注意：为了与 postcss-pxtorem (rootValue: 100) 兼容，
 * 我们需要确保计算出的 font-size 使得 100px = 1rem
 * 即：如果父元素宽度是 2500px，rootFontSize 应该是 100px
 *
 * 使用示例：
 *
 * // 方式1：多断点配置（推荐）
 * initRemAdapter(container, {
 *   breakpoints: [
 *     { width: 768, remBase: 7.5 },    // 移动端：< 768px
 *     { width: 1024, remBase: 15 },    // 平板：768px - 1024px
 *     { width: 1920, remBase: 25 },    // 桌面：1024px - 1920px
 *     { width: Infinity, remBase: 30 } // 超大屏：> 1920px
 *   ]
 * });
 *
 * // 方式2：单断点配置（向后兼容）
 * initRemAdapter(container, {
 *   remBase: 25,
 *   remBaseMobile: 7.5,
 *   breakpoint: 900
 * });
 *
 * // 方式3：根据字体大小动态换算 rem（新增）
 * initRemAdapter(container, {
 *   useFontSize: true,              // 启用字体大小换算
 *   fontSizeSelector: '.text-ref',  // 可选：指定用于获取字体大小的元素选择器
 *   fontSizeReference: 16,          // 可选：参考字体大小（px），用于比例换算
 *   fontSizeRatio: 1                // 可选：字体大小换算比例，默认 1
 * });
 */
export function initRemAdapter(containerElement, options = {}) {
  const {
    remBase = 25, // 默认 rem 基准值（桌面端），表示宽度/remBase = rootFontSize
    remBaseMobile = 7.5, // 移动端 rem 基准值（向后兼容）
    breakpoint = 900, // 移动端断点（向后兼容）
    defaultWidth = 2560, // 默认宽度
    postcssRootValue = 100, // postcss-pxtorem 的 rootValue，默认 100
    // 多断点配置：按宽度从小到大排序，每个断点包含 width 和 remBase
    // 例如：[{ width: 768, remBase: 7.5 }, { width: 1024, remBase: 15 }, { width: 1920, remBase: 25 }, { width: Infinity, remBase: 30 }]
    breakpoints = null, // 如果提供了 breakpoints，将使用多断点模式
    // 字体大小相关配置
    useFontSize = false, // 是否根据字体大小动态换算 rem
    fontSizeSelector = null, // 用于获取字体大小的选择器（如果为 null，则使用容器元素）
    fontSizeReference = null, // 参考字体大小（px），如果提供，将基于此值换算 rem
    fontSizeRatio = 1, // 字体大小换算比例，用于调整 rem 基准值
  } = options;

  let resizeObserver = null;
  let resizeTimer = null;
  let updateTimer = null; // 用于重置 isUpdating 标志位的定时器
  let lastRootFontSize = null; // 记录上一次设置的 rootFontSize，用于避免循环更新
  let isUpdating = false; // 标志位，防止在更新过程中再次触发更新

  /**
   * 获取元素的字体大小（px）
   * @param {Element} element - 目标元素
   * @returns {number} 字体大小（px）
   */
  function getFontSize(element) {
    if (!element) return null;

    const computedStyle = window.getComputedStyle(element);
    const fontSize = parseFloat(computedStyle.fontSize);
    return isNaN(fontSize) ? null : fontSize;
  }

  /**
   * 根据字体大小计算 rem 基准值和 rootFontSize
   * @param {number} fontSize - 字体大小（px）
   * @param {number} width - 容器宽度
   * @returns {Object} { remBase, rootFontSize }
   */
  function getRemBaseByFontSize(fontSize, width) {
    if (!fontSize || fontSize <= 0) {
      // 如果无法获取字体大小，回退到基于宽度的计算
      const remBase = getRemBaseByWidth(width);
      return {
        remBase,
        rootFontSize: width / remBase,
      };
    }

    let rootFontSize;
    let remBase;

    // 如果提供了参考字体大小，基于比例换算
    if (fontSizeReference) {
      // 计算字体大小比例
      const sizeRatio = fontSize / fontSizeReference;
      // rootFontSize 根据字体大小比例调整
      rootFontSize = fontSizeReference * sizeRatio * fontSizeRatio;
      // 根据 rootFontSize 和宽度计算 remBase
      remBase = width / rootFontSize;
    } else {
      // 直接使用字体大小乘以比例作为 rootFontSize
      rootFontSize = fontSize * fontSizeRatio;
      // 根据 rootFontSize 和宽度计算 remBase
      remBase = width / rootFontSize;
    }

    return { remBase, rootFontSize };
  }

  /**
   * 根据宽度获取对应的 rem 基准值
   * @param {number} width - 容器宽度
   * @returns {number} rem 基准值
   */
  function getRemBaseByWidth(width) {
    // 如果提供了多断点配置，使用多断点模式
    if (breakpoints && Array.isArray(breakpoints) && breakpoints.length > 0) {
      // 按宽度从小到大排序（确保断点顺序正确）
      const sortedBreakpoints = [...breakpoints].sort((a, b) => {
        const widthA = a.width === Infinity ? Number.MAX_SAFE_INTEGER : a.width;
        const widthB = b.width === Infinity ? Number.MAX_SAFE_INTEGER : b.width;
        return widthA - widthB;
      });

      // 找到第一个宽度大于等于当前宽度的断点
      for (const bp of sortedBreakpoints) {
        if (width <= bp.width || bp.width === Infinity) {
          return bp.remBase;
        }
      }

      // 如果没有匹配到，使用最后一个断点的 remBase
      return sortedBreakpoints[sortedBreakpoints.length - 1].remBase;
    }

    // 向后兼容：使用旧的单断点模式
    return width < breakpoint ? remBaseMobile : remBase;
  }

  /**
   * 设置 rem 值
   * 策略：由于 rem 单位在 CSS 中是相对于根元素(html)的，为了支持基于父元素的适配：
   * 1. 在容器元素上设置 CSS 变量 --rem-base（表示基于父元素的 rem 基准值）
   * 2. 动态设置 html 的 font-size，使其等于 --rem-base，这样 rem 单位就能基于父元素大小工作
   * 3. 这样组件内的 rem 单位（通过 postcss-pxtorem 转换）就能正确适配父元素大小
   */
  function setRem() {
    if (!containerElement || isUpdating) return;

    // 获取容器宽度（父元素宽度）
    // 当宽度小于断点时，使用实际宽度，但需要确保宽度稳定，避免循环更新
    const currentWidth = containerElement.clientWidth || defaultWidth;
    const width = currentWidth <= breakpoint ? currentWidth : defaultWidth;

    let rem;
    let rootFontSize;

    // 如果启用了基于字体大小的换算
    if (useFontSize) {
      // 获取用于计算字体大小的元素
      let fontSizeElement = containerElement;
      if (fontSizeSelector) {
        fontSizeElement =
          containerElement.querySelector(fontSizeSelector) || containerElement;
      }

      // 获取字体大小
      const fontSize = getFontSize(fontSizeElement);

      if (fontSize) {
        // 根据字体大小计算 rem 基准值和 rootFontSize
        const result = getRemBaseByFontSize(fontSize, width);
        rem = result.remBase;
        rootFontSize = result.rootFontSize;
      } else {
        // 如果无法获取字体大小，回退到基于宽度的计算
        rem = getRemBaseByWidth(width);
        rootFontSize = width / rem;
      }
    } else {
      // 使用基于宽度的计算（原有逻辑）
      rem = getRemBaseByWidth(width);
      rootFontSize = width / rem;
    }

    // 检查 rootFontSize 是否与上一次相同，避免不必要的更新和循环
    // 使用较小的阈值（0.1px）来避免浮点数精度问题
    // if (lastRootFontSize !== null && Math.abs(rootFontSize - lastRootFontSize) < 0.1) {
    //   return;
    // }

    // 设置更新标志位，防止在更新过程中触发新的更新
    isUpdating = true;

    // 获取当前字体大小（如果启用了字体大小换算）
    let currentFontSize = null;
    if (useFontSize) {
      let fontSizeElement = containerElement;
      if (fontSizeSelector) {
        fontSizeElement =
          containerElement.querySelector(fontSizeSelector) || containerElement;
      }
      currentFontSize = getFontSize(fontSizeElement);
    }

    // 设置到容器元素上
    if (containerElement !== document.documentElement) {
      // 在容器元素上设置 CSS 变量
      containerElement.style.setProperty("--rem-base", rootFontSize + "px");
      containerElement.style.setProperty("--container-width", width + "px");
      if (currentFontSize) {
        containerElement.style.setProperty(
          "--font-size",
          currentFontSize + "px"
        );
      }

      // 重要：设置 html 的 font-size 等于父元素的 rem 基准值
      // 这样组件内的 rem 单位就能基于父元素大小工作
      document.documentElement.style.fontSize = rootFontSize + "px";

      // 同时设置容器元素的 font-size（用于 em 单位，如果需要的话）
      containerElement.style.fontSize = rootFontSize + "px";
    } else {
      // 如果传入的是 document.documentElement，直接设置 html
      document.documentElement.style.fontSize = rootFontSize + "px";
      document.documentElement.style.setProperty(
        "--rem-base",
        rootFontSize + "px"
      );
      document.documentElement.style.setProperty(
        "--container-width",
        width + "px"
      );
      if (currentFontSize) {
        document.documentElement.style.setProperty(
          "--font-size",
          currentFontSize + "px"
        );
      }
    }

    // 记录本次设置的 rootFontSize
    lastRootFontSize = rootFontSize;
    
    // 清除之前的定时器，避免多个定时器同时运行
    clearTimeout(updateTimer);
    updateTimer = setTimeout(() => {
      isUpdating = false;
    }, 100);
  }

  /**
   * 使用 ResizeObserver 监听容器大小变化
   */
  function observeResize() {
    if (!window.ResizeObserver) {
      // 降级方案：使用 window resize 事件
      window.addEventListener("resize", handleResize);
      return;
    }

    resizeObserver = new ResizeObserver((entries) => {
      // 如果正在更新，跳过本次回调
      if (isUpdating) return;

      // 防抖处理
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        // 再次检查更新状态
        if (!isUpdating) {
          setRem();
        }
      }, 100);
    });

    resizeObserver.observe(containerElement);
  }

  /**
   * 处理 window resize（降级方案）
   */
  function handleResize() {
    // 如果正在更新，跳过本次回调
    if (isUpdating) return;

    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      // 再次检查更新状态
      if (!isUpdating) {
        setRem();
      }
    }, 100);
  }

  /**
   * 初始化
   */
  function init() {
    // 立即设置一次
    setRem();

    // 监听大小变化
    observeResize();

    // 监听 pageshow 事件（用于浏览器前进后退）
    window.addEventListener("pageshow", handlePageShow);
  }

  /**
   * 处理 pageshow 事件
   */
  function handlePageShow(e) {
    if (e.persisted) {
      setRem();
    }
  }

  /**
   * 销毁适配器
   */
  function destroy() {
    // 移除 ResizeObserver
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }

    // 清除定时器
    if (resizeTimer) {
      clearTimeout(resizeTimer);
      resizeTimer = null;
    }
    if (updateTimer) {
      clearTimeout(updateTimer);
      updateTimer = null;
    }

    // 移除事件监听
    window.removeEventListener("resize", handleResize);
    window.removeEventListener("pageshow", handlePageShow);

    // 重置样式
    if (containerElement && containerElement !== document.documentElement) {
      containerElement.style.fontSize = "";
    }
  }

  // 初始化
  init();

  // 返回销毁方法
  return {
    destroy,
    setRem, // 暴露手动设置方法
  };
}

/**
 * 便捷方法：为 Vue 组件创建 rem 适配
 */
export function createRemAdapterForComponent(vm, options = {}) {
  // 等待 DOM 更新后获取组件根元素
  return vm.$nextTick(() => {
    const containerElement = vm.$el.parentElement || vm.$el;
    return initRemAdapter(containerElement, options);
  });
}
